import { motion } from "framer-motion";

import AnimatedHeading from "../../animations/AnimatedHeading";

const fadeUp = (delay = 0) => ({
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  },
});

export default function AboutUs() {
  return (
    <section
      className="
        w-full
        overflow-hidden
        bg-[var(--bg-primary)]
        py-6
        min-[375px]:py-7
        sm:py-9
        md:py-11
        lg:py-14
      "
      style={{ fontFamily: "'Syne', sans-serif" }}
    >
      <div
        className="
          mx-auto
          w-full
          max-w-9xl
          px-4
          min-[375px]:px-5
          sm:px-6
          md:px-7
          lg:px-8
        "
      >
        {/* =====================================================
            MAIN CARD
        ====================================================== */}
        <div
          className="
            overflow-hidden
            rounded-2xl
            border
            border-[var(--border-primary)]
            bg-[var(--card-bg)]
            shadow-2xl
            sm:rounded-3xl
          "
          style={{
            boxShadow: "0 25px 50px -12px var(--shadow-color)",
          }}
        >
          <div
            className="
              grid
              w-full
              grid-cols-1
              items-stretch
              lg:grid-cols-2
            "
          >
            {/* =====================================================
                LEFT IMAGE
            ====================================================== */}
            <motion.div
              initial={{
                opacity: 0,
                x: -40,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
                amount: 0.15,
              }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                relative
                w-full
                overflow-hidden
                h-[260px]
                min-[375px]:h-[285px]
                sm:h-[350px]
                md:h-[430px]
                lg:h-full
                lg:min-h-[650px]
              "
            >
              <img
                src="https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1200&q=80"
                alt="WebBocket Office"
                loading="lazy"
                decoding="async"
                className="
                  block
                  h-full
                  w-full
                  object-cover
                  object-center
                  transition-transform
                  duration-700
                  hover:scale-[1.02]
                "
              />
            </motion.div>

            {/* =====================================================
                RIGHT CONTENT
            ====================================================== */}
            <div
              className="
                flex
                min-w-0
                flex-col
                justify-center
                px-5
                py-8
                min-[375px]:px-6
                min-[375px]:py-9
                sm:px-8
                sm:py-11
                md:px-10
                md:py-13
                lg:p-14
                lg:text-left
              "
            >
              {/* =================================================
                  SECTION LABEL
              ================================================== */}
              <motion.p
                variants={fadeUp(0)}
                initial="hidden"
                whileInView="visible"
                viewport={{
                  once: true,
                  amount: 0.2,
                }}
                className="
                  mb-4
                  text-center
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.2em]
                  min-[375px]:mb-5
                  min-[375px]:text-xs
                  min-[375px]:tracking-[0.25em]
                  sm:text-sm
                  sm:tracking-[0.3em]
                  lg:text-left
                "
                style={{
                  color: "var(--brand-primary)",
                }}
              >
                [ ABOUT WEBBOCKET ]
              </motion.p>

              {/* =================================================
                  HEADING
              ================================================== */}
              <AnimatedHeading
                as="h2"
                parts={[
                  "Driving Innovation",
                  "Through AI &",
                  "Modern Technology",
                ]}
                className="
                  mx-auto
                  mb-5
                  max-w-2xl
                  text-center
                  font-saira
                  text-[28px]
                  font-semibold
                  leading-[1.15]
                  min-[375px]:text-[30px]
                  sm:mb-7
                  sm:text-[38px]
                  md:text-[44px]
                  lg:mx-0
                  lg:mb-8
                  lg:text-left
                  lg:text-[48px]
                  lg:leading-tight
                "
                style={{
                  color: "var(--text-primary)",
                }}
              />

              {/* =================================================
                  DESCRIPTION
              ================================================== */}
              <motion.p
                variants={fadeUp(0.1)}
                initial="hidden"
                whileInView="visible"
                viewport={{
                  once: true,
                  amount: 0.2,
                }}
                className="
                  mx-auto
                  mb-7
                  max-w-2xl
                  text-center
                  text-[13px]
                  leading-6
                  min-[375px]:text-[14px]
                  min-[375px]:leading-7
                  sm:mb-9
                  sm:text-base
                  sm:leading-8
                  md:mb-10
                  lg:mx-0
                  lg:mb-10
                  lg:text-left
                "
                style={{
                  color: "var(--text-secondary)",
                }}
              >
                We help businesses embrace digital transformation through
                innovative AI, scalable software, and future-ready technology.
                Our focus is on building reliable solutions that create lasting
                value for clients across industries.
              </motion.p>

              {/* =================================================
                  MISSION + VISION
              ================================================== */}
              <motion.div
                variants={fadeUp(0.2)}
                initial="hidden"
                whileInView="visible"
                viewport={{
                  once: true,
                  amount: 0.2,
                }}
                className="
                  space-y-6
                  min-[375px]:space-y-7
                  sm:space-y-8
                "
              >
                {/* =================================================
                    MISSION
                ================================================== */}
                <div
                  className="
                    border-t-2
                    pt-4
                    text-center
                    min-[375px]:pt-5
                    sm:pt-5
                    lg:border-l-4
                    lg:border-t-0
                    lg:pl-6
                    lg:pt-0
                    lg:text-left
                  "
                  style={{
                    borderColor: "var(--brand-primary)",
                  }}
                >
                  <h3
                    className="
                      mb-2
                      font-saira
                      text-xl
                      font-bold
                      min-[375px]:text-2xl
                      sm:mb-3
                    "
                    style={{
                      color: "var(--text-primary)",
                    }}
                  >
                    Our Mission
                  </h3>

                  <p
                    className="
                      mx-auto
                      max-w-2xl
                      text-[13px]
                      leading-6
                      min-[375px]:text-[14px]
                      min-[375px]:leading-7
                      sm:text-[15px]
                      sm:leading-8
                      lg:mx-0
                    "
                    style={{
                      color: "var(--text-muted)",
                    }}
                  >
                    Revolutionize the way businesses work by delivering
                    intelligent, scalable, and future-ready digital solutions.
                    We empower organizations through innovative technology,
                    seamless user experiences, and high-performance software
                    that drives efficiency, sustainable growth, and long-term
                    success.
                  </p>
                </div>

                {/* =================================================
                    DIVIDER
                ================================================== */}
                <div
                  className="
                    mx-auto
                    h-px
                    w-full
                  "
                  style={{
                    background:
                      "linear-gradient(to right, color-mix(in srgb, var(--brand-primary) 40%, transparent), color-mix(in srgb, var(--border-primary) 100%, transparent), transparent)",
                  }}
                />

                {/* =================================================
                    VISION
                ================================================== */}
                <div
                  className="
                    border-t-2
                    pt-4
                    text-center
                    min-[375px]:pt-5
                    sm:pt-5
                    lg:border-l-4
                    lg:border-t-0
                    lg:pl-6
                    lg:pt-0
                    lg:text-left
                  "
                  style={{
                    borderColor: "var(--brand-primary)",
                  }}
                >
                  <h3
                    className="
                      mb-2
                      font-saira
                      text-xl
                      font-bold
                      min-[375px]:text-2xl
                      sm:mb-3
                    "
                    style={{
                      color: "var(--text-primary)",
                    }}
                  >
                    Our Vision
                  </h3>

                  <p
                    className="
                      mx-auto
                      max-w-2xl
                      text-[13px]
                      leading-6
                      min-[375px]:text-[14px]
                      min-[375px]:leading-7
                      sm:text-[15px]
                      sm:leading-8
                      lg:mx-0
                    "
                    style={{
                      color: "var(--text-muted)",
                    }}
                  >
                    To become a globally trusted technology partner that
                    transforms businesses through innovation, creativity, and
                    continuous improvement. We envision a future where
                    cutting-edge digital solutions simplify complex challenges
                    and create meaningful opportunities for people and
                    organizations worldwide.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}